import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  SimpleChanges,
  OnChanges,
  ViewChild
} from '@angular/core';
import { LocalStorageHelpers } from 'app/helpers';
import { AnalyticsService } from 'app/services';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { ChartConfigModel } from 'app/interfaces';
import { BaseChartDirective } from 'ng2-charts/ng2-charts';

@Component({
  selector: 'app-linear-graph',
  templateUrl: './app-linear-graph.component.html',
  styleUrls: ['./app-linear-graph.component.scss'],
  providers: [LocalStorageHelpers]
})
export class AppLinearGraphComponent implements OnInit, OnChanges {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;
  @Input() type: 'joboffers' | 'events' | 'posts' | 'polls' | 'total' | 'webinar';
  @Input() subset: number;
  @Input() id: number;
  @Input() external: boolean;
  @Input() year: number = null;
  @Input() forceAllOfAll: boolean;
  @Input() published_at: Date = null;
  @Output() ready: EventEmitter<boolean> = new EventEmitter<boolean>(true);

  public chartConfig: ChartConfigModel;
  public loading = true;
  public noResults = true;
  public daysBehind: number = this.forceAllOfAll ? 9999 : 30;

  private threshold: number;

  constructor(
    private localStorageHelpers: LocalStorageHelpers,
    private analyticsService: AnalyticsService,
    private router: Router
  ) { }

  get isCurrentYear(): boolean {
    return this.year === new Date().getFullYear();
  }

  ngOnInit(): void {
    if (this.localStorageHelpers.get('userData')) {
      const userData = JSON.parse(this.localStorageHelpers.get('userData'));
      this.threshold =
        userData['Profile'] && userData['Profile']['views_threshold']
          ? userData['Profile']['views_threshold']
          : 0;
    }

    if (this.router.url.indexOf('dashboard') > -1) {
      if (this.type === 'joboffers' && this.subset === 1) {
        this.chartConfig.navigate_label = 'Recruiting';
      }
      if (this.type === 'posts' && this.subset === 1) {
        this.chartConfig.navigate_label = 'Articles';
      }
      if (this.type === 'polls' && this.subset === 1) {
        this.chartConfig.navigate_label = 'Polls';
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.daysBehind = this.forceAllOfAll ? 9999 : 30;
    // if (changes.year && changes.year.currentValue !== null) {
    //   this.daysBehind = 9999;
    //   this.chartConfig.year = changes.year.currentValue;
    // }
    // console.log(new Date(), changes);
    this.setup();
  }

  // html select
  daysBehindChanged(event) {
    if (event !== this.chartConfig.daysBehind) {
      this.chartConfig.daysBehind = event === null ? null : parseInt(event, 10);
      this.daysBehind = this.chartConfig.daysBehind;
      this.setup();
    }
  }

  // calling api, set data, calculate and fill empty days
  setup() {
    this.loading = true;
    this.ready.emit(false);

    this.chartConfig = new ChartConfigModel(this.subset, this.daysBehind, this.year);
    this.chartConfig.for = this.type;
    this.chartConfig.id = this.id;
    this.daysBehind = this.chartConfig.daysBehind;

    // Call api based on type && id
    let apiCall: Observable<any>;
    let subscription: Subscription;
    switch (this.type) {
      case 'joboffers': {
        this.chartConfig.title = 'Job offer analytics';
        apiCall = this.analyticsService.getJobsGraph(null, this.id, this.chartConfig.daysBehind, this.year);
        subscription = apiCall.subscribe(results => {
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          this.chartConfig.bucketSize = results.bucketSize;
          if (this.subset === 0) {
            this.chartConfig.prepareDataset(0, this.external ? 'clicks' : 'applications', results['applications']);
            this.chartConfig.prepareDataset(1, 'views', results['views'], 'y-axis-2');
            this.chartConfig.ctrLabel = 'CTR';
          } else if (this.subset === 1) {
            this.chartConfig.title = 'Applications analytics';
            this.chartConfig.prepareDataset(0, 'applications', results['applications']);
          } else if (this.subset === 2) {
            this.chartConfig.title = 'Views analytics';
            this.chartConfig.prepareDataset(0, 'views', results['views']);
          }

          this.finalize(subscription);
        });
        break;
      }

      case 'webinar': {
        this.chartConfig.title = 'Webinar analytics';
        apiCall = this.analyticsService.getWebinarGraph(null, this.id, this.chartConfig.daysBehind, this.year);
        subscription = apiCall.subscribe(results => {
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          this.chartConfig.bucketSize = results.bucketSize;
          if (this.subset === 0) {
            this.chartConfig.prepareDataset(0, 'applications', results['applications']);
            this.chartConfig.prepareDataset(1, 'views', results['views'], 'y-axis-2');
            this.chartConfig.ctrLabel = 'CTR';
          } else if (this.subset === 1) {
            this.chartConfig.title = 'Participations analytics';
            this.chartConfig.prepareDataset(0, 'participations', results['applications']);
          } else if (this.subset === 2) {
            this.chartConfig.title = 'Views analytics';
            this.chartConfig.prepareDataset(0, 'views', results['views']);
          }

          this.finalize(subscription);
        });
        break;
      }

      case 'events': {
        this.chartConfig.title = 'Event analytics';
        apiCall = this.analyticsService.getEventsGraph(
          this.id,
          null,
          this.year
        );
        subscription = apiCall.subscribe(results => {
          console.log('getEventsGraph', results);
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          this.chartConfig.bucketSize = results.bucketSize;
          this.chartConfig.prepareDataset(0, 'check-ins during event', results.checkins);
          this.finalize(subscription);
        });
        break;
      }

      case 'posts': {
        this.chartConfig.title = 'Article Analytics';
        apiCall = this.analyticsService.getPostsGraph(null, this.id, this.chartConfig.daysBehind, this.year);
        subscription = apiCall.subscribe(results => {
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          // if (this.published_at) {
          //   this.chartConfig.published_at = this.chartConfig.getFormattedDate(this.published_at);
          // }
          this.chartConfig.bucketSize = results.bucketSize;

          if (this.subset === 1) {
            this.chartConfig.title = 'Views analytics';
            this.chartConfig.prepareDataset(0, 'views', results['views']);
          } else if (this.subset === 2) {
            this.chartConfig.title = 'Impressions analytics';
            this.chartConfig.prepareDataset(0, 'impressions', results['impressions']);
          } else if (this.subset === 0) {
            this.chartConfig.prepareDataset(0, 'views', results['views']);
          }
          this.finalize(subscription);
        });
        break;
      }

      case 'polls': {
        this.chartConfig.title = 'Polls Analytics';
        apiCall = this.analyticsService.getPollsGraph(null, this.id, this.chartConfig.daysBehind, this.year);
        subscription = apiCall.subscribe(results => {
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          this.chartConfig.bucketSize = results.bucketSize;

          if (this.subset === 1) {
            this.chartConfig.title = 'Answers analytics';
            this.chartConfig.prepareDataset(0, 'answers', results['answers']);
          } else if (this.subset === 2) {
            this.chartConfig.title = 'Views analytics';
            this.chartConfig.prepareDataset(0, 'views', results['views']);
          } else if (this.subset === 0) {
            this.chartConfig.prepareDataset(0, 'answers', results['answers']);
            this.chartConfig.prepareDataset(1, 'views', results['views']);
          }

          this.finalize(subscription);
        });
        break;
      }

      case 'total': {
        this.chartConfig.title = 'Total views analytics';
        this.chartConfig.cumulative = true;
        this.chartConfig.showThreshold = true;
        this.chartConfig.threshold = this.threshold;
        apiCall = this.analyticsService.getTotalViewsGraph(null, this.chartConfig.daysBehind, this.year);
        subscription = apiCall.subscribe(results => {
          this.chartConfig.start = results.start;
          this.chartConfig.end = results.end;
          this.chartConfig.bucketSize = results.bucketSize;
          this.chartConfig.prepareDataset(0, 'views', results['views']);
          this.finalize(subscription);
        });
        break;
      }

      default: {
        this.loading = false;
        this.ready.emit(true);
        this.noResults = this.chartConfig.datasetsAreEmpty;
      }
    }
  }

  finalize(subscription: Subscription) {
    subscription.unsubscribe();
    this.loading = false;
    this.ready.emit(true);
    // setTimeout(() => {
    //   if (this.chart && this.chart.chart) {
    //     this.chart.chart.update();
    //   }
    // }, 250);
    this.noResults = this.chartConfig.datasetsAreEmpty;
  }

  navigate() {
    let section = 'recruiting';
    if (this.type === 'posts') {
      section = 'articles';
    }
    if (this.type === 'polls') {
      section = 'polls';
    }
    this.router.navigate(['/' + section]);
  }
}
